import type { ContactFormStep } from './contactForm/constants';
import type { User } from '#auth-utils';

import { useLeadFlowLogger } from '~/composables/contactForm/useLeadFlowLogger';
import { EventBusKeys } from '~/core/helpers/symbols';

interface ExternalHandlerContext {
	isOpen: Ref<boolean>;
	currentStep: Ref<ContactFormStep>;
	isUserLoggedIn: ComputedRef<boolean>;
	user: ComputedRef<User | null>;
	goToOtpFormStep: (shouldSendCode?: boolean) => void;
	sendForm: () => Promise<void>;
	submitExternalForm: () => Promise<{
		success: boolean;
		step: ContactFormStep;
	}>;
	addEventSuccessMessage: () => void;
	addEventError: () => void;
}

export function useContactFormExternalHandler(context: ExternalHandlerContext) {
	const {
		isOpen,
		currentStep,
		isUserLoggedIn,
		user,
		goToOtpFormStep,
		sendForm,
		submitExternalForm,
		addEventSuccessMessage,
		addEventError
	} = context;

	const logger = useLeadFlowLogger('ContactFormExternalHandler');

	async function handleExternalFormSubmission() {
		const isUserPhoneAlreadyValidated: boolean = !!(
			user.value?.phoneNumber && user.value.hasCompletedRegistration
		);

		logger.info('handleExternalFormSubmission disparado', {
			isUserLoggedIn: isUserLoggedIn.value,
			isUserPhoneAlreadyValidated,
			currentStep: currentStep.value,
			wasOpen: isOpen.value
		});

		try {
			if (!isOpen.value) {
				isOpen.value = true;
				await nextTick();
			}

			if (isUserLoggedIn.value && isUserPhoneAlreadyValidated) {
				const result = await submitExternalForm();
				currentStep.value = result.step;

				await nextTick();
				if (result.success) {
					addEventSuccessMessage();
				} else {
					logger.warn('submitExternalForm retornou falha', {
						step: result.step
					});
					addEventError();
				}
			} else {
				await sendForm();
			}
		} catch (error) {
			logger.error('Exceção não esperada em handleExternalFormSubmission', {
				isUserLoggedIn: isUserLoggedIn.value,
				isUserPhoneAlreadyValidated,
				error
			});
			addEventError();
		}
	}

	onClientEventBus(
		useEventBus<void>(EventBusKeys.openContactModalAtOtp),
		handleExternalFormSubmission
	);

	return {
		handleExternalFormSubmission
	};
}
